import { Grid, Box, Typography, Paper, Card, CardContent, Tabs, Tab, Alert } from "@mui/material";
import React, { Fragment, useState } from "react";
import { CardSummary } from "../../../components/Common/CardSummary";
import DashboardSkeleton from "../../../components/Common/DashboardSkeleton";
import StaffLocationMap from "../../../components/Common/StaffLocationMap";
import SalesTrendsChart from "../../../components/Common/SalesTrendsChart";
import ProductPerformanceChart from "../../../components/Common/ProductPerformanceChart";
import CustomerAnalyticsCard from "../../../components/Common/CustomerAnalyticsCard";
import TopRegionsCard from "../../../components/Common/TopRegionsCard";
import {
    useGetDashboardCountQuery,
    useGetStaffLocationsQuery,
    useGetMonthlyReportsQuery,
    useGetYearlyReportsQuery,
    useGetEnhancedAnalyticsQuery,
} from "../../../store/common/commonApis";
import {
    DashboardCardIcon,
    ProductTypesCardIcon,
    SupplierTypesCardIcon,
    SalesCardIcon,
    OrdersCardIcon,
    CustomerCardIcon,
} from "../../../components/Common/Icons";

const DashboardContainer = () => {
    const { data, isLoading } = useGetDashboardCountQuery();
    const { data: staffLocationsData, isLoading: isLoadingLocations } = useGetStaffLocationsQuery();
    const { data: monthlyReports, isLoading: isLoadingMonthly } = useGetMonthlyReportsQuery();
    const { data: yearlyReports, isLoading: isLoadingYearly } = useGetYearlyReportsQuery();
    const { data: enhancedAnalytics, isLoading: isLoadingAnalytics } = useGetEnhancedAnalyticsQuery();
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    if (isLoading || isLoadingLocations || isLoadingMonthly || isLoadingYearly || isLoadingAnalytics) {
        return <DashboardSkeleton />;
    }

    // Get real data
    const dashboardData = data || {};
    const staffLocations = staffLocationsData?.staffLocations || [];

    return (
        <Fragment>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                    Dashboard
                </Typography>
            </Box>

            {/* Live KPIs Section */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={3} pb={3}>
                    <Grid item xl={2.4} lg={2.4} md={3} sm={6} xs={12}>
                        <CardSummary
                            title="Total Orders"
                            value={`${(dashboardData.summary?.total?.orders || 0).toLocaleString()}`}
                            icon={<SalesCardIcon />}
                            trend="+12.5%"
                            trendUp={true}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={3} sm={6} xs={12}>
                        <CardSummary
                            title="Total Revenue"
                            value={`₹${parseFloat(dashboardData.summary?.total?.revenue || 0).toLocaleString()}`}
                            icon={<OrdersCardIcon />}
                            trend="+15.2%"
                            trendUp={true}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={3} sm={6} xs={12}>
                        <CardSummary
                            title="Active Staff"
                            value={
                                dashboardData.userManagement?.userBreakdown?.find((user) => user.role === "staff")
                                    ?.count || 0
                            }
                            icon={<DashboardCardIcon />}
                            trend="+8.3%"
                            trendUp={true}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={3} sm={6} xs={12}>
                        <CardSummary
                            title="Active Distributors"
                            value={
                                dashboardData.userManagement?.userBreakdown?.find((user) => user.role === "distributor")
                                    ?.count || 0
                            }
                            icon={<SupplierTypesCardIcon />}
                            trend="+3.2%"
                            trendUp={true}
                        />
                    </Grid>
                    <Grid item xl={2.4} lg={2.4} md={3} sm={6} xs={12}>
                        <CardSummary
                            title="Total Customers"
                            value={dashboardData.userManagement?.totalCustomers || 0}
                            icon={<CustomerCardIcon />}
                            trend="+3.2%"
                            trendUp={true}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* B2B vs Regular Staff Breakdown */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                    Staff Performance Breakdown
                </Typography>
                <Grid container spacing={3}>
                    <Grid item lg={6} md={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#ED2024" }}>
                                B2B Staff Performance
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            p: 2,
                                            backgroundColor: "#ED2024",
                                            color: "white",
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            {dashboardData.staffBreakdown?.total?.b2b?.orders || 0}
                                        </Typography>
                                        <Typography variant="body2">Total Orders</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            p: 2,
                                            backgroundColor: "#ED2024",
                                            color: "white",
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            ₹
                                            {parseFloat(
                                                dashboardData.staffBreakdown?.total?.b2b?.revenue || 0,
                                            ).toLocaleString()}
                                        </Typography>
                                        <Typography variant="body2">Total Revenue</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid item lg={6} md={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#FFD700" }}>
                                Regular Staff Performance
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            p: 2,
                                            backgroundColor: "#FFD700",
                                            color: "black",
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            {dashboardData.staffBreakdown?.total?.regular?.orders || 0}
                                        </Typography>
                                        <Typography variant="body2">Total Orders</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            p: 2,
                                            backgroundColor: "#FFD700",
                                            color: "black",
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                            ₹
                                            {parseFloat(
                                                dashboardData.staffBreakdown?.total?.regular?.revenue || 0,
                                            ).toLocaleString()}
                                        </Typography>
                                        <Typography variant="body2">Total Revenue</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Geo-mapping and Heatmap Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item lg={6} md={12}>
                    <Paper sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Staff Location Map
                        </Typography>
                        <StaffLocationMap staffLocations={staffLocations} isLoading={isLoadingLocations} />
                    </Paper>
                </Grid>

                <Grid item lg={6} md={12}>
                    <Paper sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Region Performance
                        </Typography>
                        <Box sx={{ height: 320 }}>
                            {staffLocationsData?.regions && staffLocationsData.regions.length > 0 ? (
                                staffLocationsData.regions.map((region, index) => {
                                    const regionStaff = staffLocations.filter((s) => s.region === region);
                                    const totalSales = regionStaff.reduce((sum, staff) => sum + (staff.sales || 0), 0);
                                    const intensity = Math.min(100, Math.max(20, (totalSales / 20000) * 100));

                                    return (
                                        <Box
                                            key={region}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mb: 2,
                                                p: 2,
                                                borderRadius: 1,
                                                background: `linear-gradient(90deg, rgba(237, 32, 36, ${
                                                    intensity / 100
                                                }) 0%, rgba(237, 32, 36, ${intensity / 200}) 100%)`,
                                                color: "white",
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                                                {region}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mr: 2 }}>
                                                ₹{totalSales.toLocaleString()}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                {regionStaff.length} staff
                                            </Typography>
                                        </Box>
                                    );
                                })
                            ) : (
                                <Box
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#666",
                                    }}
                                >
                                    <Typography variant="body2">No region data available</Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Enhanced Analytics Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                    Advanced Analytics
                </Typography>
                <Grid container spacing={3}>
                    <Grid item lg={8} md={12}>
                        <SalesTrendsChart
                            salesTrends={enhancedAnalytics?.salesTrends || []}
                            isLoading={isLoadingAnalytics}
                        />
                    </Grid>
                    <Grid item lg={4} md={12}>
                        <CustomerAnalyticsCard
                            customerAnalytics={enhancedAnalytics?.customerAnalytics || {}}
                            isLoading={isLoadingAnalytics}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Product Performance & Regions */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item lg={8} md={12}>
                    <ProductPerformanceChart
                        productPerformance={enhancedAnalytics?.productPerformance || []}
                        isLoading={isLoadingAnalytics}
                    />
                </Grid>
                <Grid item lg={4} md={12}>
                    <TopRegionsCard topRegions={enhancedAnalytics?.topRegions || []} isLoading={isLoadingAnalytics} />
                </Grid>
            </Grid>

            {/* Reports Section */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Sales Reports
                </Typography>

                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
                    <Tab label="Monthly Report" />
                    <Tab label="Yearly Report" />
                </Tabs>

                {tabValue === 0 && (
                    <Box>
                        {monthlyReports?.monthly ? (
                            <Grid container spacing={2}>
                                {monthlyReports.monthly.map((month) => (
                                    <Grid item xs={12} sm={6} md={4} lg={2} key={month.month}>
                                        <Card sx={{ height: 140 }}>
                                            <CardContent sx={{ textAlign: "center", p: 2 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: "#ED2024" }}>
                                                    {month.month}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                                                    Orders: {month.orders}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "#666" }}>
                                                    Revenue: ₹{parseFloat(month.revenue).toLocaleString()}
                                                </Typography>
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color:
                                                                parseFloat(month.orderGrowth) >= 0
                                                                    ? "#4CAF50"
                                                                    : "#F44336",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        Orders: {month.orderGrowth > 0 ? "+" : ""}
                                                        {month.orderGrowth}%
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Alert severity="info">No monthly data available</Alert>
                        )}
                    </Box>
                )}

                {tabValue === 1 && (
                    <Box>
                        {yearlyReports?.yearly ? (
                            <Grid container spacing={3}>
                                {yearlyReports.yearly.map((year) => (
                                    <Grid item xs={12} sm={6} md={4} key={year.year}>
                                        <Card sx={{ height: 180 }}>
                                            <CardContent sx={{ textAlign: "center", p: 3 }}>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ fontWeight: 600, color: "#ED2024", mb: 2 }}
                                                >
                                                    {year.year}
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: "#666", mb: 1 }}>
                                                    Total Orders: {year.orders.toLocaleString()}
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: "#666", mb: 1 }}>
                                                    Revenue: ₹{parseFloat(year.revenue).toLocaleString()}
                                                </Typography>
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color:
                                                                parseFloat(year.revenueGrowth) >= 0
                                                                    ? "#4CAF50"
                                                                    : "#F44336",
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        Growth: {year.revenueGrowth > 0 ? "+" : ""}
                                                        {year.revenueGrowth}%
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Alert severity="info">No yearly data available</Alert>
                        )}
                    </Box>
                )}
            </Paper>
        </Fragment>
    );
};

export default DashboardContainer;
